import React from 'react';
import {
  Assignment as AssignmentIcon,
  AttachMoney as AttachMoneyIcon,
  DirectionsCar as DirectionsCarIcon,
  ListAlt as ListAltIcon,
  PeopleAlt as PeopleAltIcon,
  Person as PersonIcon,
  PostAdd as PostAddIcon,
  Schedule as ScheduleIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import * as Role from 'constants/role';
import { isUsuarioUsoMuto } from 'utils';
import { canViewDigitadorReport } from '../../../../helpers/digitadorReportPermission';
import { canViewInspecaoReport } from '../../../../helpers/inspecaoReportPermission';
import { renderFolhaPonto, renderPedidoVoo } from './renderMenuOption';
import { gestaoVeicularSectionShow, renderGestaoVeicular } from './renderGestaoVeicular';

const iconSx = { fontSize: 'small' };

const mapFolhaPontoToV2 = (authData, moduleConfigurations) =>
  renderFolhaPonto(authData, moduleConfigurations).map((o) => ({
    title: o.title,
    path: o.href,
    show: o.show,
    key: o.key,
  }));

const mapPedidoVooToV2 = (authData, moduleConfigurations) =>
  renderPedidoVoo(authData, moduleConfigurations).map((o) => ({
    title: o.title,
    path: o.href,
    show: o.show,
    key: o.key,
  }));

/**
 * Itens de primeiro nível do menu V2 (paridade com pages() do Sidebar legado).
 */
export const buildMainV2NavItems = ({ userRole, authData, moduleConfigurations }) => [
  {
    title: 'Meu Perfil',
    icon: <PersonIcon sx={iconSx} />,
    path: '/meu-perfil',
    show: true,
  },
  {
    title: 'Inspeção',
    icon: <AssignmentIcon sx={iconSx} />,
    show: true,
    key: 'inspecao',
    items: [
      {
        title: 'NSIRs',
        path: '/nsirs',
        isActivePath: (pathname) => pathname.includes('/nsir'),
        show: true,
        key: 'inspecao',
      },
      {
        title: 'Convocações',
        path: '/v2/convocacoes',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'inspecao',
      },
      {
        title: 'Equipamentos',
        path: '/v2/equipamentos',
        show:
          process.env.REACT_APP_EQUIPAMENTO_VIEW.split(',').includes(authData.email) &&
          !isUsuarioUsoMuto(authData.email),
        key: 'inspecao',
      },
      {
        title: 'Controle de Jornada',
        path: '/v2/controle-frequencia',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'inspecao',
      },
      ...mapFolhaPontoToV2(authData, moduleConfigurations),
      {
        title: 'Programação Diária',
        path: '/v2/programacao-diaria',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'inspecao',
      },
      ...mapPedidoVooToV2(authData, moduleConfigurations),
      {
        title: 'Relatório de Inspeção',
        path: '/v2/relatorio-inspecao',
        show: canViewInspecaoReport({ authData, userRole }),
        key: 'inspecao',
      },
      {
        title: 'Relatórios Digitadores',
        path: '/v2/relatorio-digitacao',
        show: canViewDigitadorReport({ authData, userRole }),
        key: 'inspecao',
      },
      {
        title: 'Integrações',
        path: '/v2/integracoes',
        show: canViewDigitadorReport({ authData, userRole }),
        key: 'inspecao',
      },
    ],
  },
  {
    title: 'Gestão Veicular',
    icon: <DirectionsCarIcon sx={iconSx} />,
    show: gestaoVeicularSectionShow({ authData, userRole, variant: 'v2' }),
    key: 'gestaoVeicular',
    items: renderGestaoVeicular({ authData, userRole, variant: 'v2' }),
  },
  {
    title: 'Financeiro',
    icon: <AttachMoneyIcon sx={iconSx} />,
    show: !isUsuarioUsoMuto(authData.email),
    key: 'financeiro',
    items: [
      {
        title: 'Aportes',
        path: '/v2/aportes',
        isActivePath: (pathname) => pathname.includes('/aporte'),
        show: userRole !== Role.CROQUI && userRole !== Role.DIGITADOR && !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
      {
        title: 'Cartões',
        path: '/v2/cartoes',
        show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
      {
        title: 'Notas',
        path: '/v2/notas',
        show: userRole !== Role.CROQUI && userRole !== Role.DIGITADOR && !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
      {
        title: 'Holerite',
        path: '/v2/olerites',
        show: !isUsuarioUsoMuto(authData.email),
        key: 'financeiro',
      },
    ],
  },
  {
    title: 'Estoque',
    icon: <PostAddIcon sx={iconSx} />,
    show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
    key: 'estoque',
    items: [
      {
        title: 'Estoque',
        path: '/v2/estoques',
        show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
        key: 'estoque',
      },
      {
        title: 'Pedidos',
        path: '/v2/estoque/pedidos',
        isActivePath: (pathname) => pathname.includes('/v2/estoque/pedidos'),
        show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
        key: 'estoque',
      },
      {
        title: 'Produtos',
        path: '/v2/estoque/produtos',
        isActivePath: (pathname) => pathname.includes('/v2/estoque/produtos'),
        show: process.env.REACT_APP_ESTOQUE_VIEW.split(',').includes(authData.email),
        key: 'estoque',
      },
    ],
  },
  {
    title: 'Usuários',
    icon: <PeopleAltIcon sx={iconSx} />,
    show: !isUsuarioUsoMuto(authData.email),
    key: 'usuarios',
    items: [
      {
        title: 'Listagem',
        path: '/usuarios',
        show: true,
        key: 'usuarios',
      },
      {
        title: 'Documentos e Certificados',
        path: '/usuarios-documentos',
        show:
          authData.email === 'vinicius_admin' ||
          authData.email === 'MARIA' ||
          authData.email === 'REGIANE MARIA DE OLIVEIRA' ||
          authData.email === 'JORDANIA FLORENCIO MOTA',
        key: 'usuarios',
      },
    ],
  },
  {
    title: 'Controle de Ponto',
    icon: <ScheduleIcon sx={iconSx} />,
    path: '/v2/controle-ponto',
    show: process.env.REACT_APP_CONTROLE_PONTO_VIEW.split(',').includes(authData.email),
  },
  // {
  //   title: 'Controle de Caixa',
  //   icon: <AttachMoneyIcon sx={iconSx} />,
  //   path: '/v2/controle-caixa',
  //   show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
  // },
  {
    title: 'Procedimentos e Normas',
    icon: <ListAltIcon sx={iconSx} />,
    path: '/v2/procedimentos-normas',
    show: !isUsuarioUsoMuto(authData.email),
  },
  {
    title: 'Configurações',
    icon: <SettingsIcon sx={iconSx} />,
    show: (userRole === Role.SUPER_ADMIN || userRole === Role.ADMIN) && !isUsuarioUsoMuto(authData.email),
    key: 'configurations',
    items: [
      {
        title: 'Documentos',
        path: '/v2/documentos',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Malhas',
        path: '/v2/malhas',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Gerências',
        path: '/v2/gerencias',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Polos',
        path: '/v2/polos',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Subestações',
        path: '/v2/subestacoes',
        show: true,
        key: 'configurations',
      },
      {
        title: 'Alimentadores',
        path: '/v2/alimentadores',
        show: true,
        key: 'configurations',
      },
    ],
  },
];
